import React, { memo, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Route, useHistory, withRouter } from 'react-router-dom';
import CostsSvg from 'images/chart.svg';
import CommentsSvg from 'images/comment.svg';
import SummarySvg from 'images/info-circle.svg';
import DuplicateSvg from 'images/v5/icons/copy-01.svg';
import DotsSvg from 'images/v5/icons/dots-vertical.svg';
import FileSvg from 'images/v5/icons/file-04.svg';
import FileAttachmentSvg from 'images/v5/icons/file-attachment-03.svg';
import QuantitiesSvg from 'images/v5/icons/file-check-01.svg';
import ConsumptionSvg from 'images/v5/icons/file-minus-02.svg';
import ComponentsSvg from 'images/v5/icons/layers-two-01.svg';
import ComponentsCostsSvg from 'images/v5/icons/receipt.svg';
import Rows01Svg from 'images/v5/icons/rows-01.svg';
import TrashSvg from 'images/v5/icons/trash-03.svg';
import Button from 'layout/atoms/Button/Button';
import Dropdown from 'layout/atoms/Dropdown/Dropdown';
import Loader from 'layout/atoms/Loader/Loader';
import ParameterDetails from 'layout/atoms/ParameterDetails/ParameterDetails';
import StatusBadge from 'layout/atoms/StatusBadge';
import TabsButton from 'layout/atoms/TabsButton/TabsButton';
import Grid from 'layout/molecules/Grid/Grid';
import Tools from 'layout/organisms/Tools';
import Dashboard from 'layout/templates/Dashboard/Dashboard';
import { clearBreadcrumbs, closeAllSidebars, openModal, setBreadcrumbs } from 'store/layoutSlice';
import { resetSpecificationComponentsCosts } from 'store/specificationComponentsCostsSlice';
import { resetSpecificationComponentsState } from 'store/specificationComponentsSlice';
import { resetSpecificationCosts } from 'store/specificationCostsSlice';
import { fetchSpecificationDetails, fetchSpecificationProblems, resetSpecificationDetails, setBrandNewSpecState, } from 'store/specificationDetailsSlice';
import { resetSpecificationProductionQuantities } from 'store/specificationProductionQuantitiesSlice';
import { addToast } from 'store/toastsSlice';
import { getMenuLabel } from 'utils/getMenuItem';
import Comments from './Comments';
import ComponentConsumptions from './ComponentConsumptions';
import ComponentCosts from './ComponentCosts';
import Components from './Components';
import ComponentVariants from './ComponentVariants';
import EstimatedCosts from './EstimatedCosts';
import ProductionQuantities from './ProductionQuantities';
import Summary from './Summary';
function SpecificationDetails({ match: { params: { modelSpecificationId, tab }, }, }) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s;
    const path = `/specifications/details/${modelSpecificationId}`;
    const { t } = useTranslation(['specification', 'common', 'emptyStates']);
    const dispatch = useDispatch();
    const specificationDetails = useSelector((state) => state.specificationDetails);
    const [activeTab, setActiveTab] = useState(tab);
    const history = useHistory();
    useEffect(() => {
        dispatch(fetchSpecificationDetails({ specificationId: modelSpecificationId }))
            .unwrap()
            .catch(() => {
            dispatch(addToast({
                theme: 'ghost',
                color: 'negative',
                title: t('emptyStates:SPECIFICATION_404_HEADER'),
                description: t('emptyStates:SPECIFICATION_404_DESCRIPTION'),
            }));
            setTimeout(() => {
                history.push('/specifications');
            }, 6000);
        });
        dispatch(closeAllSidebars());
        if (tab) {
            setActiveTab(tab);
        }
        else {
            setActiveTab('summary');
        }
        if (specificationDetails.refetchFlags.fetchProblems) {
            dispatch(fetchSpecificationProblems({ specificationId: modelSpecificationId }));
        }
    }, [
        t,
        dispatch,
        history,
        modelSpecificationId,
        tab,
        specificationDetails.refetchFlags.fetchProblems,
    ]);
    useEffect(() => {
        var _a;
        const specificationsLabel = getMenuLabel('/specifications', t);
        const specName = (_a = specificationDetails.item) === null || _a === void 0 ? void 0 : _a.fancyName;
        dispatch(setBreadcrumbs([
            { to: '/specifications', name: specificationsLabel },
            { to: path, name: specName || '-' },
        ]));
        return () => {
            dispatch(clearBreadcrumbs());
        };
    }, [dispatch, t, path, (_a = specificationDetails.item) === null || _a === void 0 ? void 0 : _a.fancyName]);
    useEffect(() => {
        if (specificationDetails.status.fetch === 'succeeded') {
            history.push(`/specifications/details/${modelSpecificationId}/${activeTab}`);
        }
    }, [history, modelSpecificationId, activeTab, specificationDetails.status]);
    useEffect(() => {
        return () => {
            dispatch(resetSpecificationDetails());
            dispatch(resetSpecificationProductionQuantities());
            dispatch(resetSpecificationComponentsState());
            dispatch(resetSpecificationComponentsCosts());
            dispatch(resetSpecificationCosts());
            dispatch(setBrandNewSpecState(false));
        };
    }, [dispatch]);
    const showDuplicateModelModal = useCallback(() => {
        dispatch(openModal({ name: 'duplicateSpecification' }));
    }, [dispatch]);
    const tabs = [
        { id: 'summary', icon: React.createElement(SummarySvg, null), label: t('info'), error: false },
        {
            id: 'production-quantities',
            icon: React.createElement(QuantitiesSvg, null),
            label: t('production_quantities'),
            error: !(specificationDetails === null || specificationDetails === void 0 ? void 0 : specificationDetails.brandNewSpec) &&
                ((_b = specificationDetails === null || specificationDetails === void 0 ? void 0 : specificationDetails.item) === null || _b === void 0 ? void 0 : _b.confirmationProblems) &&
                ((_c = specificationDetails.item.confirmationProblems) === null || _c === void 0 ? void 0 : _c.some((item) => item.problemType === 'zero_quantity')),
        },
        {
            id: 'components',
            icon: React.createElement(ComponentsSvg, null),
            label: t('components'),
            error: !(specificationDetails === null || specificationDetails === void 0 ? void 0 : specificationDetails.brandNewSpec) &&
                ((_d = specificationDetails === null || specificationDetails === void 0 ? void 0 : specificationDetails.item) === null || _d === void 0 ? void 0 : _d.confirmationProblems) &&
                ((_e = specificationDetails.item.confirmationProblems) === null || _e === void 0 ? void 0 : _e.some((item) => item.problemType === 'no_components')),
        },
        {
            id: 'component-consumptions',
            icon: React.createElement(ConsumptionSvg, null),
            label: t('component_consumptions'),
            error: !(specificationDetails === null || specificationDetails === void 0 ? void 0 : specificationDetails.brandNewSpec) &&
                ((_f = specificationDetails === null || specificationDetails === void 0 ? void 0 : specificationDetails.item) === null || _f === void 0 ? void 0 : _f.confirmationProblems) &&
                ((_g = specificationDetails.item.confirmationProblems) === null || _g === void 0 ? void 0 : _g.some((item) => item.problemType === 'no_component_consumption')),
        },
        {
            id: 'component-variants',
            icon: React.createElement(Rows01Svg, null),
            label: t('component_variants'),
            error: !(specificationDetails === null || specificationDetails === void 0 ? void 0 : specificationDetails.brandNewSpec) &&
                ((_h = specificationDetails === null || specificationDetails === void 0 ? void 0 : specificationDetails.item) === null || _h === void 0 ? void 0 : _h.confirmationProblems) &&
                (((_j = specificationDetails.item.confirmationProblems) === null || _j === void 0 ? void 0 : _j.some((item) => item.problemType === 'incomplete_variant')) ||
                    ((_k = specificationDetails.item.confirmationProblems) === null || _k === void 0 ? void 0 : _k.some((item) => item.problemType === 'incorrect_variant'))),
        },
        {
            id: 'component-costs',
            icon: React.createElement(ComponentsCostsSvg, null),
            label: t('component_costs'),
            error: !(specificationDetails === null || specificationDetails === void 0 ? void 0 : specificationDetails.brandNewSpec) &&
                ((_l = specificationDetails === null || specificationDetails === void 0 ? void 0 : specificationDetails.item) === null || _l === void 0 ? void 0 : _l.estimatedCostCalculationProblems) &&
                ((_o = (_m = specificationDetails === null || specificationDetails === void 0 ? void 0 : specificationDetails.item) === null || _m === void 0 ? void 0 : _m.estimatedCostCalculationProblems) === null || _o === void 0 ? void 0 : _o.some((item) => item.problemType === 'missing_variant_price')),
        },
        { id: 'estimated-costs', icon: React.createElement(CostsSvg, null), label: t('estimated_costs'), error: false },
        { id: 'comments', icon: React.createElement(CommentsSvg, null), label: t('comments'), error: false },
    ];
    if (specificationDetails.status.fetch === 'succeeded') {
        return (React.createElement(React.Fragment, null,
            React.createElement(Dashboard, { headerAside: React.createElement(React.Fragment, null,
                    ((_p = specificationDetails === null || specificationDetails === void 0 ? void 0 : specificationDetails.item.confirmationProblems) === null || _p === void 0 ? void 0 : _p.length) > 0 ||
                        ((_q = specificationDetails === null || specificationDetails === void 0 ? void 0 : specificationDetails.item.estimatedCostCalculationProblems) === null || _q === void 0 ? void 0 : _q.length) > 0 ? (React.createElement(StatusBadge, { size: "l", theme: "fill", color: "negative", onClick: () => dispatch(openModal({ name: 'specificationProblems' })) }, t('specification_problems'))) : null,
                    React.createElement(Button, { align: "left", color: "primary", leftIcon: React.createElement(FileSvg, null), to: `/specifications/pdf/${modelSpecificationId}` }, t('GENERATE_PDF_BUTTON')),
                    React.createElement(Dropdown, { trigger: React.createElement(Button, { theme: "ghost", shape: "rounded", leftIcon: React.createElement(DotsSvg, null) }), testId: "specification-actions" },
                        React.createElement(Button, { align: "left", theme: "ghost", leftIcon: React.createElement(FileAttachmentSvg, null), to: `/specifications/pdf-components/${modelSpecificationId}` }, t('GENERATE_PDF_WITH_COMPONENTS_BUTTON')),
                        (specificationDetails === null || specificationDetails === void 0 ? void 0 : specificationDetails.item.state) === 'PRODUCTION' && (React.createElement(Button, { align: "left", theme: "ghost", leftIcon: React.createElement(DuplicateSvg, null), onClick: showDuplicateModelModal }, t('common:duplicate_as_draft'))),
                        (specificationDetails === null || specificationDetails === void 0 ? void 0 : specificationDetails.item.state) !== 'PRODUCTION' && (React.createElement(Button, { align: "left", theme: "ghost", color: "danger", leftIcon: React.createElement(TrashSvg, null), onClick: () => dispatch(openModal({
                                name: 'removeSpecification',
                                data: {
                                    id: modelSpecificationId,
                                    itemCode: specificationDetails.item.itemCode,
                                    prevPath: '/specifications',
                                },
                            })) }, t('common:delete'))))) },
                React.createElement(Grid, { vp: 24, hp: 24 },
                    React.createElement(ParameterDetails, { size: "l", label: (_r = specificationDetails === null || specificationDetails === void 0 ? void 0 : specificationDetails.item) === null || _r === void 0 ? void 0 : _r.itemCode, value: (_s = specificationDetails === null || specificationDetails === void 0 ? void 0 : specificationDetails.item) === null || _s === void 0 ? void 0 : _s.fancyName }),
                    React.createElement(TabsButton, { tabs: tabs, activeTab: activeTab, onClick: (e) => setActiveTab(e) }),
                    React.createElement(Route, { exact: true, path: `${path}/summary` },
                        React.createElement(Summary, null)),
                    React.createElement(Route, { exact: true, path: `${path}/production-quantities` },
                        React.createElement(ProductionQuantities, null)),
                    React.createElement(Route, { exact: true, path: `${path}/components` },
                        React.createElement(Components, null)),
                    React.createElement(Route, { exact: true, path: `${path}/component-consumptions` },
                        React.createElement(ComponentConsumptions, null)),
                    React.createElement(Route, { exact: true, path: `${path}/component-variants` },
                        React.createElement(ComponentVariants, null)),
                    React.createElement(Route, { exact: true, path: `${path}/component-costs` },
                        React.createElement(ComponentCosts, null)),
                    React.createElement(Route, { exact: true, path: `${path}/estimated-costs` },
                        React.createElement(EstimatedCosts, null)),
                    React.createElement(Route, { path: `${path}/comments/:projectionId?` },
                        React.createElement(Comments, null)))),
            React.createElement(Tools, null)));
    }
    return (React.createElement(Dashboard, null,
        React.createElement(Loader, null)));
}
export default memo(withRouter(SpecificationDetails));
